import classNames from "classnames";
import { MatchGauge } from "PFComponents/resource_metrics/match_gauge";
import { IconName } from "PFTheme/graphics/icons";
import { CustomTypes, MatchedProfile } from "PFTypes";
import { ComponentProps, MouseEvent, ReactElement, ReactNode, SyntheticEvent, useCallback } from "react";

import { Card } from "../card";
import ProfileCardSkills from "./parts/profile_card_skills";
import ProfileCardTitle from "./parts/profile_card_title";
import css from "./profile_card.module.scss";

const AVATAR_SIZE = 80;

type GaugeProps = Partial<Omit<ComponentProps<typeof MatchGauge>, "match">>;

type ProfileCardProps = {
  className?: string;
  customTypes: CustomTypes;
  profileResponse: MatchedProfile;
  actions?: ReactNode;
  headerActions?: ReactNode;
  embeded?: boolean;
  topRightCornerNode?: ReactNode;
  children?: ReactElement;
  footer?: ReactElement;
  icon?: IconName;
  isColumn?: boolean;
  handleClick?: () => void;
  handleIconClick?: (e: SyntheticEvent) => void;
  outlook?: boolean;
  gaugeProps?: GaugeProps;
  // makes profiles names hashed and avatar blank for fair-recruitement reasons
  isUnbiasedMode: boolean;
};

/**
 * @deprecated Use application\components\profile_card
 */
const ProfileCard = ({
  className,
  customTypes,
  profileResponse,
  actions,
  headerActions,
  embeded,
  topRightCornerNode,
  children,
  footer,
  icon,
  isColumn,
  handleClick,
  handleIconClick,
  isUnbiasedMode,
  outlook,
  gaugeProps = {}
}: ProfileCardProps) => {
  const { profile, scores } = profileResponse;
  const skillsStyle = embeded ? { borderTop: 0 } : {};

  const href = `/profiles/${profile.id}`;

  const onAvatarClick = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      outlook ? window.open(`${location.origin}${href}`, "_blank") : handleClick?.();
    },
    [href, outlook, handleClick]
  );

  const guts = (
    <>
      <div className={css.header}>
        <ProfileCardTitle
          avatarSize={AVATAR_SIZE}
          customTypes={customTypes}
          handleClick={handleClick}
          handleIconClick={handleIconClick}
          href={href}
          icon={icon}
          isUnbiasedMode={isUnbiasedMode}
          profile={profile}
          onAvatarClick={onAvatarClick}
          outlook={outlook}
        />

        <div className={css.topRightCorner}>
          {scores && <MatchGauge match={{ scores }} className={css.match} {...gaugeProps} />}
          {headerActions && <div className={css.headerActions}>{headerActions}</div>}
          {topRightCornerNode}
        </div>
      </div>

      <ProfileCardSkills
        profileResponse={profileResponse}
        style={skillsStyle}
        isUnbiasedMode={isUnbiasedMode}
      />

      {children}

      {actions && (
        <>
          <hr className={css.separator} />
          <div className={classNames(css.skillsAndActions, { [css.skillsAndActionsColumn]: isColumn })}>
            {!isColumn && <small> &nbsp;</small>}
            {actions && <div className={classNames({ [css.actions]: isColumn })}>{actions}</div>}
          </div>
        </>
      )}

      {footer}
    </>
  );

  return embeded ? (
    <div className={classNames(css.embededWrap, className)}>{guts}</div>
  ) : (
    <Card className={className} paddingVariant="lg">
      {guts}
    </Card>
  );
};

export default ProfileCard;
